import { useMemo, useState } from 'react';
import type React from 'react';
import type { JSONContent } from '@tiptap/core';
import { useTranslation } from '@course-authoring/i18n';
import {
  isRichTextContentEmpty,
  useTheme,
  type LessonCompleteBlock,
  type LessonCompleteTitleLevel,
} from '@course-authoring/shared-types';
import { useIsMobileViewport } from './hooks/useIsMobileViewport';
import { SCROLL_COMPLETION_ANCHOR_ATTRIBUTE } from './hooks/useScrollCompletion';
import { LESSON_COMPLETE_PRESET_IMAGES } from './lessonCompletePresetImages';
import {
  createRichTextContentResetStyles,
  createRichTextRendererExtensions,
  safeGenerateRichTextHTML,
} from './richTextRenderer';
import { createContentBlockStyles } from './styles/contentBlocks';
import { getContentBlockHeadingTypography } from './styles/titleTypography';
import { RichTextContent } from './RichTextContent';

const descriptionResetStyles = createRichTextContentResetStyles(
  'lesson-complete-description-rich',
  '0.6em'
);

interface LessonCompleteRendererProps {
  block: LessonCompleteBlock;
  isStandalone?: boolean;
  onContinue?: () => void;
  onSaveExit?: () => void;
}

export function LessonCompleteRenderer({
  block,
  isStandalone = true,
  onContinue,
  onSaveExit,
}: LessonCompleteRendererProps): React.ReactElement {
  const { t } = useTranslation();
  const { tokens } = useTheme();
  const isMobile = useIsMobileViewport(tokens.layout.mobileBreakpoint);
  // NA-205: lesson-complete body copy holds 20px through mid-range, drops to 16px ≤576px.
  const isSmallMobile = useIsMobileViewport(tokens.layout.smallMobileBreakpoint);
  const richTextExtensions = useMemo(() => createRichTextRendererExtensions(), []);
  const blockStyles = useMemo(() => createContentBlockStyles(tokens), [tokens]);
  const [isButtonHovered, setIsButtonHovered] = useState(false);

  const titleLevel: LessonCompleteTitleLevel = block.titleLevel ?? 'h2';
  const hasImage = Boolean(block.imageUrl);
  const isPreset = hasImage && LESSON_COMPLETE_PRESET_IMAGES.some((p) => p.src === block.imageUrl);
  const hasTitle = block.title.trim().length > 0;
  const hasDescription = !isRichTextContentEmpty(block.description as JSONContent);

  const headingStyle = getContentBlockHeadingTypography(tokens, titleLevel, {
    isMobile,
    isSmallMobile,
  });

  const descriptionHtml = useMemo(() => {
    if (!hasDescription) {
      return '';
    }
    return safeGenerateRichTextHTML(block.description as JSONContent, richTextExtensions);
  }, [block.description, hasDescription, richTextExtensions]);

  const imageMaxWidth = isMobile ? '200px' : '278px';

  const styles: Record<string, React.CSSProperties> = {
    section: {
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'transparent',
      padding: `${tokens.spacing["2xl"]} 0`,
    },
    content: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: isMobile ? tokens.spacing.lg : '44px',
    },
    textGroup: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '15px',
    },
    imageContainer: {
      display: 'flex',
      justifyContent: 'center',
      flexShrink: 0,
    },
    image: isPreset
      ? {
          maxWidth: imageMaxWidth,
          width: '100%',
          height: 'auto',
          objectFit: 'contain',
        }
      : {
          maxWidth: imageMaxWidth,
          borderRadius: tokens.radius.md,
        },
    textContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
      maxWidth: isMobile ? '90%' : '826px',
    },
    title: {
      margin: 0,
      color: tokens.text.primary,
      fontFamily: tokens.typography.fontFamily,
      fontSize: headingStyle.size,
      fontWeight: headingStyle.weight,
      lineHeight: headingStyle.lineHeight,
    },
    richDescription: {
      margin: 0,
      color: tokens.text.primary,
      fontFamily: tokens.typography.fontFamily,
      fontSize: isSmallMobile ? tokens.typography.mobile.body.size : tokens.typography.body.size,
      fontWeight: isSmallMobile ? tokens.typography.mobile.body.weight : tokens.typography.body.weight,
      lineHeight: isSmallMobile
        ? tokens.typography.mobile.body.lineHeight
        : tokens.typography.body.lineHeight,
    },
  };

  const HeadingTag = titleLevel === 'h4' ? 'h4' : titleLevel;

  return (
    <section style={styles.section}>
      <div style={styles.content}>
        <div style={styles.textGroup}>
          {hasImage ? (
            <div style={styles.imageContainer}>
              <img
                src={block.imageUrl}
                alt={block.imageAlt ?? ''}
                style={styles.image}
                loading="lazy"
              />
            </div>
          ) : null}
          {hasTitle || hasDescription ? (
            <div style={styles.textContainer}>
              {hasTitle ? <HeadingTag style={styles.title}>{block.title}</HeadingTag> : null}
              {hasDescription ? (
                <>
                  <style>{descriptionResetStyles}</style>
                  <RichTextContent
                    className="lesson-complete-description-rich"
                    style={styles.richDescription}
                    html={descriptionHtml}
                  />
                </>
              ) : null}
            </div>
          ) : null}
        </div>
        <button
          type="button"
          style={{
            ...blockStyles.actionButton,
            ...(isButtonHovered ? blockStyles.actionButtonHover : {}),
          }}
          onMouseEnter={() => setIsButtonHovered(true)}
          onMouseLeave={() => setIsButtonHovered(false)}
          onClick={isStandalone ? onSaveExit : onContinue}
          data-testid={isStandalone ? 'lesson-complete-save-exit' : 'lesson-complete-continue'}
          // NA-363: the button is the scroll-completion boundary — the lesson
          // completes once it is fully visible, not once the block's bottom
          // padding has also scrolled into view.
          {...{ [SCROLL_COMPLETION_ANCHOR_ATTRIBUTE]: 'true' }}
        >
          {isStandalone ? t('save_exit_bar.save_exit') : t('timed_course.continue')}
        </button>
      </div>
    </section>
  );
}

LessonCompleteRenderer.displayName = 'LessonCompleteRenderer';
